export interface AccountRetentionRule {
  lastActivityAt: string | null;
  rule: string;
  withinRetention: boolean;
}

export interface AccountRetention {
  /** Null when the subject has not been de-identified. */
  anonymisedAt: string | null;
  email: string;
  eligibleAt: string;
  flaggedAt: string | null;
  isEligible: boolean;
  lastActivityAt: string;
  lastActivityType: string;
  retentionYears: number;
  rules: AccountRetentionRule[];
}

// Wire values are camelCase-first-letter, matching the API's enum serialisation.
export const deidentifyOutcome = {
  deidentified: 'deidentified',
  reverted: 'reverted',
  skipped: 'skipped',
  refused: 'refused',
  eligible: 'eligible'
} as const;

export type DeidentifyOutcome =
  (typeof deidentifyOutcome)[keyof typeof deidentifyOutcome];

export const retentionStatus = {
  flagged: 'flagged',
  anonymised: 'anonymised',
  excluded: 'excluded'
} as const;

export type RetentionStatus =
  (typeof retentionStatus)[keyof typeof retentionStatus];

export interface DeidentifyColumnChange {
  name: string;
  /** The replacement value, or null where the column is cleared. */
  value: string | null;
}

export interface DeidentifyTableCount {
  /** Populated on a preview only; a commit or revert reports counts alone. */
  columns: DeidentifyColumnChange[] | null;
  name: string;
  rows: number;
}

export interface DeidentifySubjectResult {
  accountId: number | null;
  email: string;
  outcome: DeidentifyOutcome;
  reason: string | null;
  tables: DeidentifyTableCount[];
}

export interface DeidentifyResult {
  /** Owned by the API so the client never keeps its own copy of the window. */
  revertWindowDays: number;
  subjects: DeidentifySubjectResult[];
}

// Subjects are addressed by flag id from the grid, so the email never travels in a URL. The API also
// accepts accountIds and emails for the account page and the erasure path.
export interface DeidentifyRequest {
  accountIds?: number[];
  emails?: string[];
  flagIds?: number[];
}

export const auditAction = {
  deidentified: 'deidentified',
  reverted: 'reverted'
} as const;

export type AuditAction = (typeof auditAction)[keyof typeof auditAction];

export interface AuditTable {
  /** Column names only. Values live in the snapshot and are gone once it is purged. */
  columns: string[];
  name: string;
  rows: number;
}

export interface AuditEntry {
  action: AuditAction;
  performedAt: string;
  /**
   * Named as they were named at the time, not as they are named now. Null means the scheduled job
   * rather than a person.
   */
  performedBy: string | null;
  tables: AuditTable[];
}

export interface RetentionAudit {
  entries: AuditEntry[];
}
